import React, { FC, useEffect, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Typography,
	Grid,
	TextField,
	FormControl,
	Select,
	MenuItem,
	FormHelperText,
	Skeleton
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import WifiIcon from "@mui/icons-material/Wifi";
import { useForm, Controller } from "react-hook-form";
import ConfirmationDialog from "../../common/ConfirmationDialog";

interface StaticIpFormData {
	ipAddress: string;
	gateway: string;
	subnetMask: string;
	customSubnetMask?: string;
	dns1: string;
	dns2: string;
	interface: string;
}

interface NetworkConfig {
	ethernet?: {
		connected: boolean;
		ip?: string;
		gateway?: string;
		device?: string;
	};
	wifi?: {
		connected: boolean;
		ip?: string;
		gateway?: string;
		device?: string;
	};
	gateway?: string;
}

const StaticIpConfiguration: FC = () => {
	const [networkConfig, setNetworkConfig] = useState<NetworkConfig | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [configLoading, setConfigLoading] = useState<boolean>(true);
	const [showCustomSubnet, setShowCustomSubnet] = useState<boolean>(false);
	const [showDhcpConfirmDialog, setShowDhcpConfirmDialog] = useState<boolean>(false);

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors, isSubmitting }
	} = useForm<StaticIpFormData>({
		defaultValues: {
			ipAddress: "",
			gateway: "",
			subnetMask: "/24",
			dns1: "8.8.8.8",
			dns2: "8.8.4.4",
			interface: ""
		}
	});

	const subnetMask = watch("subnetMask");

	useEffect(() => {
		loadNetworkConfig();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setShowCustomSubnet(subnetMask === "custom");
	}, [subnetMask]);

	const loadNetworkConfig = async () => {
		try {
			setConfigLoading(true);
			const response = await fetch(`http://${window.location.hostname}:81/api/network-config`);
			const data = await response.json();

			if (response.ok) {
				setNetworkConfig(data);
				populateFormWithCurrentData(data);
			}
		} catch (error) {
			console.error("Error loading network configuration:", error);
		} finally {
			setConfigLoading(false);
		}
	};

	const populateFormWithCurrentData = (config: NetworkConfig) => {
		if (config.gateway) {
			setValue("gateway", config.gateway);
		}

		// Use Ethernet interface if available, otherwise use WiFi interface
		let interfaceName = null;
		let currentIP = null;

		if (config.ethernet?.connected) {
			interfaceName = config.ethernet.device;
			currentIP = config.ethernet.ip;
		} else if (config.wifi?.connected) {
			interfaceName = config.wifi.device;
			currentIP = config.wifi.ip;
		}

		if (interfaceName) {
			setValue("interface", interfaceName);
		}

		// Suggest a static IP based on current gateway
		if (config.gateway && currentIP) {
			const gatewayParts = config.gateway.split(".");
			if (gatewayParts.length === 4) {
				const suggestedIP = `${gatewayParts[0]}.${gatewayParts[1]}.${gatewayParts[2]}.100`;
				setValue("ipAddress", suggestedIP);
			}
		}
	};

	const validateIpAddress = (ip: string) => {
		const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
		return ipRegex.test(ip) || "Please enter a valid IP address";
	};

	const validateSubnetMask = (subnetMask: string | undefined) => {
		if (!subnetMask) return "Subnet mask is required";

		// Check if it's a valid CIDR notation or dotted decimal
		const cidrRegex = /^\/[0-9]{1,2}$/;
		const dottedRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

		if (cidrRegex.test(subnetMask) || dottedRegex.test(subnetMask)) {
			return true;
		}
		return "Invalid subnet mask format. Use CIDR notation (/24) or dotted decimal (255.255.255.0)";
	};

	const onSubmit = async (data: StaticIpFormData) => {
		try {
			setLoading(true);

			// Get subnet mask
			let subnetMask = data.subnetMask;
			if (subnetMask === "custom") {
				subnetMask = data.customSubnetMask || "";
				if (!subnetMask) {
					alert("Please enter a custom subnet mask");
					return;
				}
			}

			const requestData = {
				ipAddress: data.ipAddress,
				gateway: data.gateway,
				subnetMask: subnetMask,
				dns1: data.dns1,
				dns2: data.dns2,
				interface: data.interface
			};

			const response = await fetch(`http://${window.location.hostname}:81/api/configure-static-ip`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify(requestData)
			});

			const result = await response.json();

			if (response.ok) {
				alert(result.message || "Static IP configured successfully");
				// Wait a moment for network to restart, then refresh
				setTimeout(() => {
					loadNetworkConfig();
				}, 5000);
			} else {
				throw new Error(result.error || "Failed to configure static IP");
			}
		} catch (error) {
			console.error("Error configuring static IP:", error);
			alert(error instanceof Error ? error.message : "Failed to configure static IP");
		} finally {
			setLoading(false);
		}
	};

	const [selectedInterface, setSelectedInterface] = useState<string | null>(null);

	const handleEnableDhcp = async () => {
		if (!networkConfig) {
			alert("No connected interface available");
			return;
		}

		// Use Ethernet interface if available, otherwise use WiFi interface
		let interfaceName = null;

		if (networkConfig.ethernet?.connected) {
			interfaceName = networkConfig.ethernet.device;
		} else if (networkConfig.wifi?.connected) {
			interfaceName = networkConfig.wifi.device;
		}

		if (!interfaceName) {
			alert("No connected interface available");
			return;
		}

		setSelectedInterface(interfaceName);
		setShowDhcpConfirmDialog(true);
	};

	const confirmEnableDhcp = async () => {
		if (!selectedInterface) return;

		try {
			setLoading(true);

			const response = await fetch(`http://${window.location.hostname}:81/api/enable-dhcp`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify({
					interface: selectedInterface
				})
			});

			const result = await response.json();

			if (response.ok) {
				alert(result.message || "DHCP enabled successfully");
				// Wait a moment for network to restart, then refresh
				setTimeout(() => {
					loadNetworkConfig();
				}, 5000);
			} else {
				throw new Error(result.error || "Failed to enable DHCP");
			}
		} catch (error) {
			console.error("Error enabling DHCP:", error);
			alert(error instanceof Error ? error.message : "Failed to enable DHCP");
		} finally {
			setLoading(false);
			setShowDhcpConfirmDialog(false);
			setSelectedInterface(null);
		}
	};

	return (
		<>
			<Box
				className="network-setting-section-wrapper"
				component="form"
				noValidate
				onSubmit={handleSubmit(onSubmit)}
				sx={{
					background: "#ffffff",
					borderRadius: "12px",
					boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
					border: "1px solid rgba(0, 0, 0, 0.06)"
				}}
			>
				<Typography variant="h6" color="var(--color-primary-main)">
					Configure Static IP
				</Typography>

				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<Box className="network-setting-wrapper">
							<Box className="network-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Static IP Address
								</Typography>
								<Typography variant="body2" color="#616161">
									Enter the desired static IP address for your machine
								</Typography>
							</Box>

							<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
								{configLoading ? (
									<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: "4px" }} />
								) : (
									<Controller
										name="ipAddress"
										control={control}
										rules={{
											required: "IP address is required",
											validate: validateIpAddress
										}}
										render={({ field }) => (
											<TextField
												{...field}
												fullWidth
												size="small"
												variant="filled"
												placeholder="192.168.1.100"
												error={!!errors.ipAddress}
												helperText={errors.ipAddress?.message}
												InputProps={{
													disableUnderline: true,
													hiddenLabel: true,
													classes: { root: "filled-input-root" }
												}}
											/>
										)}
									/>
								)}
							</Box>
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<Box className="network-setting-wrapper">
							<Box className="network-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Gateway/Router IP
								</Typography>
								<Typography variant="body2" color="#616161">
									Your router's IP address (usually ends with .1)
								</Typography>
							</Box>

							<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
								{configLoading ? (
									<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: "4px" }} />
								) : (
									<Controller
										name="gateway"
										control={control}
										rules={{
											required: "Gateway IP is required",
											validate: validateIpAddress
										}}
										render={({ field }) => (
											<TextField
												{...field}
												fullWidth
												size="small"
												variant="filled"
												placeholder="192.168.1.1"
												error={!!errors.gateway}
												helperText={errors.gateway?.message}
												InputProps={{
													disableUnderline: true,
													hiddenLabel: true,
													classes: { root: "filled-input-root" }
												}}
											/>
										)}
									/>
								)}
							</Box>
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<Box className="network-setting-wrapper">
							<Box className="network-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Subnet Mask
								</Typography>
								<Typography variant="body2" color="#616161">
									Choose the subnet mask for your network
								</Typography>
							</Box>

							<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
								{configLoading ? (
									<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: "4px" }} />
								) : (
									<Controller
										name="subnetMask"
										control={control}
										rules={{ required: "Subnet mask is required" }}
										render={({ field }) => (
											<FormControl fullWidth size="small" error={!!errors.subnetMask}>
												<Select
													{...field}
													variant="filled"
													disableUnderline
													hiddenLabel
													classes={{ root: "filled-input-root" }}
													sx={{ border: "1px solid rgba(0, 0, 0, 0.08)" }}
												>
													<MenuItem value="/24">/24 (255.255.255.0) - Default</MenuItem>
													<MenuItem value="/16">/16 (255.255.0.0)</MenuItem>
													<MenuItem value="/8">/8 (255.0.0.0)</MenuItem>
													<MenuItem value="/25">/25 (255.255.255.128)</MenuItem>
													<MenuItem value="/26">/26 (255.255.255.192)</MenuItem>
													<MenuItem value="/27">/27 (255.255.255.224)</MenuItem>
													<MenuItem value="/28">/28 (255.255.255.240)</MenuItem>
													<MenuItem value="/29">/29 (255.255.255.248)</MenuItem>
													<MenuItem value="/30">/30 (255.255.255.252)</MenuItem>
													<MenuItem value="/32">/32 (255.255.255.255) - Single Host</MenuItem>
													<MenuItem value="custom">Custom (Enter manually)</MenuItem>
												</Select>
												{errors.subnetMask && <FormHelperText>{errors.subnetMask.message}</FormHelperText>}
											</FormControl>
										)}
									/>
								)}
							</Box>
						</Box>
					</Grid>

					{showCustomSubnet && (
						<Grid item xs={12} md={6}>
							<Box className="network-setting-wrapper">
								<Box className="network-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Custom Subnet Mask
									</Typography>
									<Typography variant="body2" color="#616161">
										Enter subnet mask in CIDR notation (/24) or dotted decimal (255.255.255.0)
									</Typography>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
									{configLoading ? (
										<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: "4px" }} />
									) : (
										<Controller
											name="customSubnetMask"
											control={control}
											rules={{
												required: showCustomSubnet ? "Custom subnet mask is required" : false,
												validate: showCustomSubnet ? validateSubnetMask : undefined
											}}
											render={({ field }) => (
												<TextField
													{...field}
													fullWidth
													size="small"
													variant="filled"
													placeholder="255.255.255.0 or /24"
													error={!!errors.customSubnetMask}
													helperText={errors.customSubnetMask?.message}
													InputProps={{
														disableUnderline: true,
														hiddenLabel: true,
														classes: { root: "filled-input-root" }
													}}
												/>
											)}
										/>
									)}
								</Box>
							</Box>
						</Grid>
					)}

					<Grid item xs={12} md={6}>
						<Box className="network-setting-wrapper">
							<Box className="network-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Primary DNS
								</Typography>
								<Typography variant="body2" color="#616161">
									Google DNS (8.8.8.8)
								</Typography>
							</Box>

							<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
								{configLoading ? (
									<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: "4px" }} />
								) : (
									<Controller
										name="dns1"
										control={control}
										rules={{ validate: validateIpAddress }}
										render={({ field }) => (
											<TextField
												{...field}
												fullWidth
												size="small"
												variant="filled"
												placeholder="8.8.8.8"
												error={!!errors.dns1}
												helperText={errors.dns1?.message}
												InputProps={{
													disableUnderline: true,
													hiddenLabel: true,
													classes: { root: "filled-input-root" }
												}}
											/>
										)}
									/>
								)}
							</Box>
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<Box className="network-setting-wrapper">
							<Box className="network-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Secondary DNS
								</Typography>
								<Typography variant="body2" color="#616161">
									Google DNS (8.8.4.4)
								</Typography>
							</Box>

							<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
								{configLoading ? (
									<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: "4px" }} />
								) : (
									<Controller
										name="dns2"
										control={control}
										rules={{ validate: validateIpAddress }}
										render={({ field }) => (
											<TextField
												{...field}
												fullWidth
												size="small"
												variant="filled"
												placeholder="8.8.4.4"
												error={!!errors.dns2}
												helperText={errors.dns2?.message}
												InputProps={{
													disableUnderline: true,
													hiddenLabel: true,
													classes: { root: "filled-input-root" }
												}}
											/>
										)}
									/>
								)}
							</Box>
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<Box className="network-setting-wrapper">
							<Box className="network-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Network Interface
								</Typography>
								<Typography variant="body2" color="#616161">
									Choose the network interface to configure
								</Typography>
							</Box>

							<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
								{configLoading ? (
									<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: "4px" }} />
								) : (
									<Controller
										name="interface"
										control={control}
										rules={{ required: "Network interface is required" }}
										render={({ field }) => (
											<FormControl fullWidth size="small" error={!!errors.interface}>
												<Select
													{...field}
													variant="filled"
													disableUnderline
													hiddenLabel
													classes={{ root: "filled-input-root" }}
													sx={{ border: "1px solid rgba(0, 0, 0, 0.08)" }}
												>
													<MenuItem value="">Select interface...</MenuItem>
													<MenuItem value="eth0">Ethernet (eth0)</MenuItem>
													<MenuItem value="wlan0">WiFi (wlan0)</MenuItem>
												</Select>
												{errors.interface && <FormHelperText>{errors.interface.message}</FormHelperText>}
											</FormControl>
										)}
									/>
								)}
							</Box>
						</Box>
					</Grid>
				</Grid>

				<Box sx={{ display: "flex", gap: 2 }}>
					<Button
						type="submit"
						variant="contained"
						color="primary"
						startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
						disabled={loading || isSubmitting}
					>
						{loading ? "Applying..." : "Apply Static IP"}
					</Button>
					<Button
						type="button"
						variant="outlined"
						color="warning"
						startIcon={<WifiIcon />}
						onClick={handleEnableDhcp}
						disabled={loading}
					>
						Enable DHCP
					</Button>
				</Box>
			</Box>

			<ConfirmationDialog
				width="sm"
				open={showDhcpConfirmDialog}
				title="Enable DHCP"
				heading="Are you sure you want to enable DHCP? This will remove the static IP configuration."
				onClose={() => {
					setShowDhcpConfirmDialog(false);
					setSelectedInterface(null);
				}}
				onConfirm={confirmEnableDhcp}
			/>
		</>
	);
};

export default StaticIpConfiguration;
